import { Box, Typography } from '@mui/material';
import React from 'react';

// Import types from local pricing types
import type { MarketplaceService } from '../../types/pricing';
import { getServiceCardPrice } from '../../utils/serviceCardPrice';

interface SimplePricingDisplayProps {
  service: MarketplaceService;
  className?: string;
}

/**
 * Price block for marketplace CARDS — headline, escalation schedule, and
 * one-time fees. No selection UI; the detail page owns configuration.
 *
 * All pricing logic lives in `utils/serviceCardPrice`, which `ServiceGridSSR`
 * also calls. Keeping the computation in one place is what stops the
 * pre-hydration HTML and the hydrated render from disagreeing — they
 * previously used separate formatters with different decimal precision and
 * different range handling, so the price visibly changed on hydration.
 *
 * Render contract: the headline is a fixed shape (`[From ]<amount><unit>`) so
 * the price lands in the same position on every tile and a buyer can scan a
 * column. Everything variable goes in the slots beneath it.
 */
export const SimplePricingDisplay: React.FC<SimplePricingDisplayProps> = ({
  service,
  className,
}) => {
  const { headline, schedule, fees } = getServiceCardPrice(service);

  if (!headline) return null;

  return (
    <Box className={className}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 'bold',
          color: 'text.primary',
          mb: schedule || fees.length > 0 ? 0.5 : 1,
        }}
      >
        {headline}
      </Typography>

      {/* Escalation disclosure. Present whenever the price changes over the
          life of the contract — the buyer does not control this variation,
          so hiding it below the fold is not an option. */}
      {schedule && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.7rem', display: 'block', fontWeight: 600 }}
        >
          {schedule}
        </Typography>
      )}

      {fees.length > 0 && (
        <Box sx={{ mt: 0.25 }}>
          {fees.map((fee, index) => (
            <Typography
              key={index}
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.7rem', display: 'block' }}
            >
              {fee}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};
